import { ExtensionApiFactory } from '~cdw-ae/common/frontend/browser-extension-api';

import { setUpdateNeeded, setUpdateVersion } from './version-update.state';

import { environment } from '../../../environments/environment';
import { rootStore } from '../extension-store/extension.store';
import { StateStatus } from '../extension-store/interfaces/extension-store.interfaces';
import UtilsSerice from '../utils/utils.service';

interface ChromeVersion {
  major: number;
  minor: number;
}

const openRemoteHelp = async () => {
  const extensionApi = ExtensionApiFactory();
  const state = rootStore.getState();
  const url = UtilsSerice.appendUrlArguments(`${environment.apiServer}/support`, {
    method: 'help',
    domain: state.customerState.customerSettings?.customerId ?? 'NOT_SET',
    uuid: state.deviceDetails.details.id,
    source: 'popup',
  });

  await extensionApi.createTab({ url });
};

const showFixVersionNotification = () => {
  const NOTIFICATION_ID = 'fixVersionId';
  const extensionApi = ExtensionApiFactory();

  const fixVersionOptions = {
    type: 'basic',
    iconUrl: 'assets/images/gb_default_icon.png',
    title: "Let's update Chrome.",
    message: 'Please read the "About Chrome" page carefully and follow Google\'s instructions to update Chrome.',
    buttons: [
      {
        title: 'I got it!',
      },
      {
        title: 'I need more help',
      },
    ],
    requireInteraction: true,
  };
  extensionApi.clearNotification(NOTIFICATION_ID);
  extensionApi.showNotification(NOTIFICATION_ID, fixVersionOptions);
  extensionApi.setNotificationListener(async (notificationId: string, buttonIndex: number) => {
    if (notificationId === NOTIFICATION_ID) {
      if (buttonIndex === 0) {
        // Will be prompted again next time Button is clicked
      }
      if (buttonIndex === 1) {
        await openRemoteHelp();
      }
      extensionApi.clearNotification(NOTIFICATION_ID);
    }
  });
};

const setLastNotificationTime = async () => {
  const extensionApi = ExtensionApiFactory();
  await extensionApi.setLocalStorage({ lastNotificationTime: String(new Date().getTime()) });
};

const inNotificationWindow = async () => {
  const extensionApi = ExtensionApiFactory();
  const notificationTimes = await extensionApi.getLocalStorage<{ lastNotificationTime: string }>(
    'lastNotificationTime'
  );

  if (!notificationTimes) {
    return true;
  }
  const currentTime = new Date().getTime();
  return currentTime - Number(notificationTimes.lastNotificationTime) > 15 * 60 * 1000;
};

const showUpdatePopup = async () => {
  // ensure the notification is shown only once in a 15 minute window
  if (!(await inNotificationWindow())) {
    return;
  }
  await setLastNotificationTime();

  const NOTIFICATION_ID = 'updateNotificationId';

  const extensionApi = ExtensionApiFactory();

  const notificationOptions = {
    type: 'basic',
    iconUrl: 'assets/images/gb_default_icon.png',
    title: 'Chrome is out of date',
    message: 'You need to update Chrome.\nClick "Ok" to open the update page, or "Later" to get reminded later.',
    buttons: [
      {
        title: 'Ok',
      },
      {
        title: 'Later',
      },
    ],
    requireInteraction: true,
  };

  extensionApi.clearNotification(NOTIFICATION_ID);
  extensionApi.showNotification(NOTIFICATION_ID, notificationOptions);
  extensionApi.setNotificationListener(async (notificationId: string, buttonIndex: number) => {
    if (notificationId === NOTIFICATION_ID) {
      if (buttonIndex === 0) {
        await extensionApi.createTab({ url: 'chrome://os-settings/help' });
      }
      if (buttonIndex === 1) {
        // Will be prompted again when the extension is reloaded
      }
      extensionApi.clearNotification(NOTIFICATION_ID);
    }
  });
};

// Specific to Chrome version of Major.Minor.Patch.Build
// We treat the Build as the minor version
const parseVersion = (version: string): ChromeVersion => {
  const matcher = /(\d+)\.?\d*\.\d*\.(\d+)/;
  const match = version.match(matcher) ?? [];

  return {
    major: Number(match[1]),
    minor: Number(match[2]),
  };
};

export const updateRequired = (
  currentOsVersion: string,
  latestStableVersion: string,
  popupThreshold: number,
  minorVersionThreshold: number,
  restrictChromeOsVersionUpdates: boolean,
  restrictChromeOsMinorVersion: boolean,
  chromeOsVersionMax: number,
  chromeOsMinorVersionMax: number
): boolean => {
  const { major: currentMajor, minor: currentMinor } = parseVersion(currentOsVersion);
  const { major: latestMajor, minor: latestMinor } = parseVersion(latestStableVersion);

  if (currentMajor < latestMajor) {
    return (
      latestMajor - currentMajor >= popupThreshold &&
      (!restrictChromeOsVersionUpdates || latestMajor <= chromeOsVersionMax)
    );
  }

  if (currentMajor === latestMajor) {
    return (
      latestMinor - currentMinor >= minorVersionThreshold &&
      (!restrictChromeOsMinorVersion || latestMinor <= chromeOsMinorVersionMax)
    );
  }
  return false;
};

const VersionUpdateService = {
  init: async () => {
    UtilsSerice.debugLog('VersionUpdateService.init');

    const unSub = rootStore.subscribe(async () => {
      const state = rootStore.getState();
      const currentOsVersion = state.deviceDetails.details.OSVersion;
      const latestStableVersion = state.deviceDetails.deviceResource?.latestStableVersion;
      if (
        state.customerState?.customerSettings?.settings.allowChromeOsUpdateReminders &&
        state.versionUpdateState.status === StateStatus.IDLE &&
        currentOsVersion &&
        latestStableVersion
      ) {
        const isUpdateRequired = updateRequired(
          currentOsVersion,
          latestStableVersion,
          state.customerState.customerSettings.settings.popupThreshold,
          state.customerState.customerSettings.settings.minorVersionThreshold,
          state.customerState.customerSettings.settings.restrictChromeOsVersionUpdates,
          state.customerState.customerSettings.settings.restrictChromeOsMinorVersion,
          state.customerState.customerSettings.settings.chromeOsVersionMax,
          state.customerState.customerSettings.settings.chromeOsMinorVersionMax
        );

        if (isUpdateRequired) {
          const extensionApi = ExtensionApiFactory();
          await extensionApi.setIconBadge({ backgroundColor: 'red', text: '!', textColor: 'black' }); // Add a badge to call out an action in needed.
          unSub();
          rootStore.dispatch(setUpdateVersion(latestStableVersion));
          rootStore.dispatch(setUpdateNeeded(isUpdateRequired));
          await showUpdatePopup();
        }
      }
    });
  },
  showFixVersionNotification,
  openRemoteHelp,
};

export default VersionUpdateService;
